import React, {
  useCallback, useContext, useEffect, useRef, useState,
} from 'react';
import {
  Row, Col,
} from 'react-bootstrap';
import { useHistory, useLocation, useParams } from 'react-router';
import PaymentService from 'services/Payment';
import IpService from 'services/Ip';
import Storage from 'utils/Storage';
import AdyenCheckout from '@adyen/adyen-web';
import handlePromise from 'utils/Promise';
import '@adyen/adyen-web/dist/adyen.css';
import ErrorComponent from 'components/Error';
import { LoadingContext } from 'context/Loading';
import { languageSelector, translationsAdyen } from 'utils/Common';
import SuccessComponent from 'components/Success';
import { CHECKOUT } from 'constants/routes';
import { RawFinancialCard, SloraFinancialCard } from 'types/financialCard';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import i18n from 'i18next';

type ParamTypes = {
  username: string,
  language: string,
};

const clientKey = process.env.REACT_APP_CLIENT_KEY;
const environment = process.env.REACT_APP_ENVIRONMENT;
const redirectResult = process.env.REACT_APP_REDIRECT_RESULT || '';
const returnUrl = process.env.REACT_APP_RETURN_URL || '';

function useQuery() {
  const { search } = useLocation();

  return React.useMemo( () => new URLSearchParams( search ), [search] );
}

function AddCardAdyen() {
  const { t } = useTranslation();
  const { toggleLoading } = useContext( LoadingContext );
  const { language, username } = useParams<ParamTypes>();
  const query = useQuery();
  const token = query.get( 'token' );
  const [error, setError] = useState<any>( false );
  const success = false;
  const action = useRef<any>( false );
  const history = useHistory();

  const addFinancialCard = useCallback( async ( data: RawFinancialCard ) => {
    const [, responseClientIp, responseDataClientIp] = await handlePromise(
      IpService.getClientPublicIP(),
    );
    const awsToken = Storage.get( 'sloraadyen:token' );
    const cardToSend: SloraFinancialCard = {
      cardNumber: data.paymentMethod.encryptedCardNumber,
      cardHolderName: data.paymentMethod.holderName,
      expiryMonth: data.paymentMethod.encryptedExpiryMonth,
      expiryYear: data.paymentMethod.encryptedExpiryYear,
      securityCode: data.paymentMethod.encryptedSecurityCode,
      isDefault: true,
      token: awsToken || '',
      browserInfo: {
        userAgent: data.browserInfo.userAgent,
        acceptHeader: data.browserInfo.acceptHeader,
        language: data.browserInfo.language,
        colorDepth: data.browserInfo.colorDepth,
        screenHeight: data.browserInfo.screenHeight,
        screenWidth: data.browserInfo.screenWidth,
        timeZoneOffset: data.browserInfo.timeZoneOffset,
        javaEnabled: data.browserInfo.javaEnabled,
      },
      origin: window.location.origin.toString(),
      redirectResultUrl: redirectResult,
      returnUrl,
      shopperIP: responseClientIp.ok ? responseDataClientIp : '0.0.0.0',
    };
    try {
      const [errors, response, responseData] = await handlePromise(
        PaymentService.createFinancialCard( username, cardToSend ),
      );
      return {
        errorPayment: errors,
        errorStatus: response.status,
        responseDataPayment: responseData,
      };
    } catch ( e ) {
      Sentry.captureException( e );
      return { errorPayment: JSON.stringify( e ), errorStatus: null, responseDataPayment: null };
    }
  }, [] );

  const makeDetailsCall = useCallback( async ( data: any ) => {
    const dataToSend = {
      actionResult: {
        md: '',
        paRes: '',
        paymentData: data.paymentData,
        requestId: action.current?.requestId || '',
        challengeResult: data.details['threeds2.challengeResult'] ?? '',
        fingerPrint: data.details['threeds2.fingerprint'] ?? '',
        type: action.current?.type?.toUpperCase() ?? '',
      },
    };
    try {
      const [errors, response, responseData] = await handlePromise(
        PaymentService.createFinancialCard( username, dataToSend ),
      );
      return {
        errorPayment: errors,
        errorStatus: response.status,
        responseDataPayment: responseData,
      };
    } catch ( e: any ) {
      Sentry.captureException( e );
      return { errorPayment: JSON.stringify( e ), responseDataPayment: null };
    }
  }, [] );

  const getPaymentMethods = useCallback( async ( ) => {
    const [, response, responseData] = await handlePromise(
      PaymentService.getPaymentMethods(),
    );
    if ( response.ok && !!responseData.paymentMethods ) {
      const configuration = {
        paymentMethodsResponse: responseData,
        clientKey,
        locale: languageSelector( language ),
        translations: translationsAdyen,
        environment,
        amount: { value: 0, currency: 'EUR' },
        openFirstPaymentMethod: true,
        onSubmit: async ( state: any, dropin: any ) => {
          dropin.setStatus( 'ready' );
          toggleLoading( true );
          if ( state.isValid ) {
            const {
              responseDataPayment, errorPayment,
              errorStatus,
            } = await addFinancialCard( state.data );
            if ( !errorPayment && responseDataPayment.action ) {
              action.current = {
                type: responseDataPayment.action.type,
                requestId: responseDataPayment.requestId,
              };
              dropin.handleAction( responseDataPayment.action );
            } else if ( !errorPayment && responseDataPayment ) {
              dropin.setStatus( 'success', { message: t( 'methodSuccessfully' ) } );
              setTimeout( () => {
                history.push( CHECKOUT.replace( ':status', 'success' ) );
              }, 1000 );
            } else {
              Sentry.captureMessage( errorPayment );
              if ( errorStatus === 500 || errorStatus === 502
                  || errorStatus.toString().indexOf( 5 ) === 0 ) {
                setError( t( 'error.addFinancialCard' ) );
              } else {
                setError( errorPayment.toString() );
              }
            }
          } else {
            setError( 'OnSubmit INVALID CARD' );
            Sentry.captureMessage( 'OnSubmit INVALID CARD' );
          }
          setTimeout( () => {
            toggleLoading( false );
          }, 800 );
        },
        onAdditionalDetails: async ( state: any, dropin: any ) => {
          dropin.setStatus( 'ready' );
          try {
            const {
              responseDataPayment,
              errorStatus,
              errorPayment,
            } = await makeDetailsCall( state.data );
            if ( !errorPayment && responseDataPayment.action ) {
              action.current = {
                type: responseDataPayment.action.type,
                requestId: responseDataPayment.requestId,
              };
              dropin.handleAction( responseDataPayment.action );
            } else if ( !errorPayment && responseDataPayment ) {
              dropin.setStatus( 'success', { message: t( 'methodSuccessfully' ) } );
              setTimeout( () => {
                history.push( CHECKOUT.replace( ':status', 'success' ) );
              }, 1000 );
            } else {
              Sentry.captureMessage( errorPayment );
              if ( errorStatus === 500 || errorStatus === 502
                  || errorStatus.toString().indexOf( 5 ) === 0 ) {
                setError( t( 'error.addFinancialCard' ) );
              } else {
                setError( errorPayment.toString() );
              }
            }
          } catch ( e ) {
            Sentry.captureException( e );
            setError( e );
          }
        },
        paymentMethodsConfiguration: {
          card: {
            hasHolderName: true,
            holderNameRequired: true,
            enableStoreDetails: false,
            hideCVC: false, // Change this to true to hide the CVC field for stored cards
            name: t( 'payment.cardName' ),
            environment,
          },
        },
      };
      const checkout = new AdyenCheckout( configuration );
      checkout.create( 'dropin' ).mount( '#dropin-container' );
    } else {
      const errorMessage = response?.data?.error?.toString()
        ? `${response?.data?.message?.toString()}-${response?.data?.error
          ?.toString()}-${response?.data?.status?.toString()}` : response.originalError.message.toString();
      Sentry.captureException( errorMessage );
      setError( errorMessage );
    }
  }, [] );

  useEffect( () => {
    let awsToken = Storage.get( 'sloraadyen:token' );
    if ( !awsToken && token ) {
      Storage.set( 'sloraadyen:token', token );
    }
    awsToken = Storage.get( 'sloraadyen:token' );
    Sentry.setTag( 'locale', language );
    Sentry.setUser( { username } );
    Sentry.setContext( 'accessToken', {
      awsToken,
    } );

    // console.log( 'username', username );
    // console.log( 'awsToken', awsToken );
    // console.log( 'language', language );

    if ( awsToken && username && language ) {
      getPaymentMethods();
    } else {
      setError( 'Invalid parameters' );
    }
    i18n.changeLanguage( language );
  }, [] );

  return (
    <>
      {!error && !success && (
      <Row className="">
        <Col md className="card--light d-flex align-items-center justify-content-center flex-column">
          <div id="dropin-container" />
          <div id="card-container" />
        </Col>
      </Row>
      )}
      {!!error && (
      <Row className="">
        <Col md>
          <ErrorComponent
            error={error}
            reload
          />
        </Col>
      </Row>
      )}
      {success && (
      <Row className="">
        <Col md>
          <SuccessComponent />
        </Col>
      </Row>
      )}
    </>
  );
}

export default AddCardAdyen;
